import { useProgramsRTK } from '@entities/programs/model.ts';
import cn from 'classnames';
import { useCallback, useMemo } from 'react';
import { MainMenu } from '@widgets/programs/dropdown-menu';
import { useProgramVideos } from '@features/programs';
import { ArrowShortLeft } from '@shared/assets';
import { useWindowWidth } from '@shared/hooks';
import type { Program } from '@shared/types/programs';
import { IconButton, ListBody, PageHead, PageTitle } from '@shared/ui';
import { Loader } from '@src/library/components/common/index.ts';
import { useVideoClick } from 'library/hooks/programs';
import { Aside, Head, Main } from './frames';
import ProgramLanding from '../ProgramLanding';
import * as st from './VideoProgram.module.scss';

type Props = {
  isModal?: boolean;
  onClose: () => void;
  program?: Program;
};

const VideoProgram = ({ onClose, program }: Props) => {
  const { day, resetSelectedDay, onDaySelect, videos, hasPromo } = useProgramVideos(program);
  const windowWidth = useWindowWidth();
  const { makeProgramFirstOpenFalse } = useProgramsRTK();
  const {
    mode,
    currentVideoInPlayer,
    description,
    about,
    onAboutClick,
    selectedId,
    onRecommendationClick,
    onVideoClick,
    changeMode,
    isNeedPay,
    removeVideoId,
    scrollToSavedPos,
    videoBlockRef,
    saveCurrentPos,
    currentActiveRecommendation,
    isCurrentVideoAvailableBySchedule,
    onCurrentVideoNotAvailableClick,
    getIsVideoAvailableByScheduleFunc,
  } = useVideoClick(program);

  const isThereOnlyOneVideoWithoutTrailerAndCoachComment =
    program?.videos.length === 1 && !program?.promo_video && !program?.individual;

  const isNeedToShowAside = useMemo(() => {
    let output = false;
    if (windowWidth > 768) {
      if (!isThereOnlyOneVideoWithoutTrailerAndCoachComment) {
        output = true;
      }
    } else if (!mode) {
      output = true;
    }
    return output;
  }, [windowWidth, mode, isThereOnlyOneVideoWithoutTrailerAndCoachComment]);

  const title = () => {
    if (windowWidth > 768) return 'Видеопрограмма';
    if (mode === 'video') return 'Видео';
    if (mode === 'promo') return 'Трейлер';
    if (mode === 'days') return 'Выберите день';
    return 'Видеопрограмма';
  };

  const onCloseProgram = useCallback(
    () => program && program.first_open && makeProgramFirstOpenFalse(program.pk),
    [program, makeProgramFirstOpenFalse]
  );

  return (
    <>
      <PageTitle title={title()} />
      {windowWidth <= 768 && (
        <PageHead
          Left={
            <IconButton
              variant="dark"
              onClick={() => {
                changeMode(undefined);
                setTimeout(scrollToSavedPos, 5);
                onCloseProgram();
                // alert('kek');
              }}
              id="close-program"
            >
              <ArrowShortLeft />
            </IconButton>
          }
        />
      )}

      {(!mode || windowWidth > 768) && (
        <Head
          program={program}
          selectedDay={day}
          changeMode={changeMode}
          resetSelectedDay={resetSelectedDay}
          onClose={() => {
            onClose();
            removeVideoId();
            onCloseProgram();
          }}
          Menu={
            program ? (
              <MainMenu
                saveCurrentPos={saveCurrentPos}
                program={program}
                authorId={program?.author?.coach?.id}
              />
            ) : undefined
          }
        />
      )}

      <ListBody className={st.body}>
        {program ? (
          <>
            {(windowWidth > 768 || !mode) && <ProgramLanding program={program} />}
            <div id="video" className={cn(st.program__content, mode && st.content_main)}>
              <div className={st.program__wrapper}>
                {(windowWidth > 768 || mode) && (
                  <Main
                    isCurrentVideoAvailableBySchedule={isCurrentVideoAvailableBySchedule}
                    onCurrentVideoNotAvailableClick={onCurrentVideoNotAvailableClick}
                    isNeedPay={isNeedPay}
                    video={currentVideoInPlayer}
                    mode={mode}
                    selectedDay={day}
                    onDaySelect={onDaySelect}
                    description={description}
                    about={about}
                    program={program}
                    onRedirect={saveCurrentPos}
                    currentActiveRecommendation={currentActiveRecommendation}
                    changeMode={changeMode}
                    isThereOnlyOneVideoWithoutTrailerAndCoachComment={
                      isThereOnlyOneVideoWithoutTrailerAndCoachComment
                    }
                  />
                )}

                {isNeedToShowAside && (
                  <Aside
                    getIsVideoAvailableByScheduleFunc={getIsVideoAvailableByScheduleFunc}
                    scrollBlockRef={videoBlockRef}
                    program={program}
                    mode={mode}
                    onAboutClick={onAboutClick}
                    selectedId={selectedId}
                    onRecommendationClick={onRecommendationClick}
                    currentActiveRecommendation={currentActiveRecommendation}
                    onVideoClick={onVideoClick}
                    programVideos={videos}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <Loader />
        )}
      </ListBody>
    </>
  );
};

export default VideoProgram;
